import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Helmet } from "react-helmet-async";

function LLMSTxtGenerator() {
  const [domain, setDomain] = useState("");
  const [content, setContent] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processed, setProcessed] = useState(0);
  const [total, setTotal] = useState(0);
  const [entries, setEntries] = useState([]);
  const [tab, setTab] = useState("txt");

  // ... all your imports
const fetchRaw = (url) => fetch(url); // ✅ no allorigins for robots/sitemaps
const fetchHTML = (url) => fetch(`https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`); // ✅ only for HTML pages

const findSitemaps = async (base) => {
  const sitemaps = new Set();
  try {
    const res = await fetchRaw(`${base}/robots.txt`);
    if (res.ok) {
      const text = await res.text();
      text.split("\n").forEach((line) => {
        const match = line.match(/sitemap:\s*(.*)/i);
        if (match) sitemaps.add(match[1].trim());
      });
    }
  } catch (e) {}

  if (sitemaps.size === 0) {
    ["/sitemap.xml", "/sitemap_index.xml", "/sitemap1.xml"].forEach((guess) => {
      sitemaps.add(base + guess);
    });
  }

  return Array.from(sitemaps);
};

const collectUrls = async (url, acc, parser) => {
  if (acc.length >= 500) return;
  try {
    const res = await fetchRaw(url);
    if (!res.ok) return;
    const xmlText = await res.text();
    const xml = parser.parseFromString(xmlText, "application/xml");

    const sitemapLocs = Array.from(xml.querySelectorAll("sitemap > loc"));
    if (sitemapLocs.length > 0) {
      for (const loc of sitemapLocs) {
        await collectUrls(loc.textContent.trim(), acc, parser);
        if (acc.length >= 500) break;
      }
    } else {
      const urlLocs = Array.from(xml.querySelectorAll("url > loc"));
      acc.push(...urlLocs.map((n) => n.textContent.trim()));
    }
  } catch {}
};

const fetchMeta = async (url, parser) => {
  try {
    const res = await fetchHTML(url);
    if (!res.ok) return null;
    const html = await res.text();
    const doc = parser.parseFromString(html, "text/html");
    const title = (doc.querySelector("title")?.textContent || url).trim();
    const description =
      doc.querySelector('meta[name="description"]')?.content ||
      doc.querySelector('meta[property="og:description"]')?.content || "";
    return { url, title, description: description.trim() };
  } catch {
    return null;
  }
};

const generate = async () => {
  setLoading(true);
  setError(null);
  setContent("");
  setProcessed(0);
  setTotal(0);
  setEntries([]);
  setTab("txt");

  try {
    let base = domain.trim();
    if (!/^https?:\/\//i.test(base)) base = "https://" + base;
    base = base.replace(/\/$/, "");

    const urlObj = new URL(base);
    const host = urlObj.hostname;
    const altHost = host.startsWith("www.") ? host.replace(/^www\./, "") : "www." + host;
    const altBase = `${urlObj.protocol}//${altHost}`;

    const sitemapUrls = Array.from(new Set([
      ...(await findSitemaps(base)),
      ...(await findSitemaps(altBase)),
    ]));

    if (sitemapUrls.length === 0) throw new Error("No sitemap found");

    const parser = new DOMParser();
    const urls = [];
    for (const sitemap of sitemapUrls) {
      await collectUrls(sitemap, urls, parser);
      if (urls.length >= 500) break;
    }

    const uniqueUrls = Array.from(new Set(urls)).filter((u) => {
      try {
        const uObj = new URL(u);
        return !uObj.search && !/\/page\/(\d+)/i.test(uObj.pathname);
      } catch {
        return false;
      }
    });

    const limitedUrls = uniqueUrls.slice(0, 500);
    setTotal(limitedUrls.length);

    const processedEntries = [];
    for (const u of limitedUrls) {
      const meta = await fetchMeta(u, parser);
      if (meta) processedEntries.push(meta);
      setProcessed((p) => p + 1);
    }

    if (processedEntries.length === 0) throw new Error("No pages found");

    const siteHost = new URL(processedEntries[0].url).host;
    const lines = processedEntries.map(
      (e) => `- [${e.title}](${e.url}): ${e.description}`
    );
    const txt = `# ${siteHost} llms.txt\n\n` + lines.join("\n");

    setContent(txt);
    setEntries(processedEntries);
    setTab("txt");
  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
};


  const downloadFile = () => {
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "llms.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Helmet>
        <title>LLMS.txt Generator - Seotic</title>
        <meta
          name="description"
          content="Generate an llms.txt file from your website sitemap"
        />
      </Helmet>
      <Navbar />
      <div className="min-h-screen px-5 bg-gradient-to-t from-slate-100 to-white">
        <div className="container mx-auto pt-20 pb-5">
          <div className="flex flex-col justify-center items-center mb-8">
            <h2 className="text-center text-base/7 uppercase text-slate-400">PUBLIC TOOL</h2>
            <p className="mx-auto mt-2 max-w-lg text-center text-4xl font-semibold tracking-tight text-pretty text-gray-950 sm:text-5xl">
              LLMS.txt generator and guide for beginners
            </p>
          </div>
          <p className="text-center text-slate-600 mb-8">
            Enter a domain to generate an llms.txt file.
          </p>
          <div className="flex mb-6 max-w-md mx-auto">
            <input
              type="text"
              placeholder="example.com"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              className="flex-grow px-5 py-3 border border-slate-200 mr-2 rounded-lg focus:outline-none"
            />
            <button
              onClick={generate}
              className="px-5 py-3 bg-yellow-300 text-yellow-900 rounded-lg hover:bg-yellow-400"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="28"
                height="28"
                color="currentColor"
                fill="none"
              >
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"></circle>
                <path
                  d="M16 12H8M16 12C16 11.2998 14.0057 9.99153 13.5 9.5M16 12C16 12.7002 14.0057 14.0085 13.5 14.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </svg>
            </button>
          </div>
          {loading && (
            <div className="my-10">
              <div className="max-w-md mx-auto h-2 bg-slate-200 rounded">
                <div
                  className="h-2 bg-yellow-300 rounded"
                  style={{
                    width:
                      total > 0 ? `${Math.round((processed / total) * 100)}%` : "0%",
                  }}
                ></div>
              </div>
              <p className="text-center mt-2">Processing...</p>
            </div>
          )}
          {error && <p className="text-center text-red-500 my-10">{error}</p>}
          {content && (
            <div className="bg-white border rounded-lg p-10">
              <div className="flex mb-5 space-x-5">
                <button
                  className={`${
                    tab === "txt"
                      ? "text-slate-900"
                      : "text-slate-400 hover:text-slate-900"
                  } pb-1`}
                  onClick={() => setTab("txt")}
                >
                  <i class='bx bx-file bx-fw'></i> Generated llms.txt
                </button>
                <button
                  className={`${
                    tab === "urls"
                      ? "text-slate-900"
                      : "text-slate-400 hover:text-slate-900"
                  } pb-1`}
                  onClick={() => setTab("urls")}
                >
                 <i class='bx bx-globe bx-fw' ></i> Processed URLs
                </button>
              </div>
              {tab === "txt" ? (
                <>
                  <h2 className="flex items-center mb-5 text-2xl font-medium tracking-tight text-gray-950">
                    llms.txt     <button
                    onClick={downloadFile}
                    className="ml-2 text-sm px-3 py-2 bg-yellow-300 text-yellow-900 rounded-lg hover:bg-yellow-400"
                  >
                    <i class='bx bx-cloud-download bx-fw' ></i> Download
                  </button>

                  </h2>
                  <pre className="font-medium bg-slate-50 rounded-lg p-5 tracking-tight text-slate-500 leading-[1.5em] overflow-auto whitespace-pre-wrap">
                    {content}
                  </pre>
                              </>
              ) : (
                <>
                                  <ul className="font-medium tracking-tight text-slate-500 leading-[1.5em] overflow-auto max-h-96 list-none">
                    {entries.map((e) => (
                      <li key={e.url} className="mb-2">
                        <a
                          href={e.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className=""
                        >
                          <i class='bx bx-check-circle bx-fw text-green-500' ></i> {e.url}
                        </a>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-5">
            <div className="bg-white border rounded-lg p-10">
              <h2 className="mb-5 text-2xl font-medium tracking-tight text-gray-950">
                What is llms.txt?
              </h2>
              <p className="font-medium tracking-tight text-slate-500 leading-[1.5em]">
                An llms.txt file lists important pages on your site with short
                descriptions. It helps language models understand and reference
                your content more accurately.
              </p>
            </div>
            <div className="bg-white border rounded-lg p-10">
              <h3 className="mb-5 text-2xl font-medium tracking-tight text-gray-950">
                Why is it important?
              </h3>
              <ul className="font-medium tracking-tight text-slate-500 leading-[1.5em] space-y-5">
                <li>Gives AI models context about your pages.</li>
                <li>Improves visibility of key content in AI answers.</li>
                <li>Offers a standardized place for LLMs to look for info.</li>
              </ul>
            </div>
            <div className="bg-white border rounded-lg p-10">
              <h3 className="mb-5 text-2xl font-medium tracking-tight text-gray-950">
                How to submit llms.txt
              </h3>
              <ol className="font-medium tracking-tight text-slate-500 leading-[1.5em] space-y-5">
                <li>Generate and download the file using this tool.</li>
                <li>Upload <code>llms.txt</code> to your website's root folder.</li>
                <li>
                  Visit <code>yourdomain.com/llms.txt</code> to make sure it's
                  reachable.
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default LLMSTxtGenerator;